// PO12-F1 (docs/build-briefs/PO12-F1-form-first-offer.md): the pure half of the add-offer request.
import type { CreateVendorItemOfferInput } from "./api.ts";

export interface VendorOfferFormValues {
  vendorId: string;
  liquorProductId: string;
  vendorSku?: string;
  orderUnit?: string;
  packSize?: number;
  unitsPerCase?: number;
  costCents?: number;
  /** Set only when the manager actually chose it. Undefined lets the server decide (a first offer is preferred). */
  preferred?: boolean;
}

/** `preferred` is left out of the body unless the manager set it, so the server default applies. */
export function buildVendorOfferRequest(values: VendorOfferFormValues): CreateVendorItemOfferInput {
  const { preferred, ...rest } = values;
  return preferred === undefined ? rest : { ...rest, preferred };
}

// INVUNIT1 (settings-setup-01): the manager-word reading of an offer's price. The stored fields do not change:
// `costCents` is the price of ONE ORDER UNIT, and for a case `packSize` (also saved as units per case) is how many of
// the item's own units one case holds, the divisor the shared cost calculator already uses.

/** The wording and number for one order unit, or null when the form cannot say yet. */
export interface VendorOfferCostReading {
  /** "case", "bottle", "keg", "can" or "unit": what the price in the cost field is for. */
  orderUnitNoun: string;
  /** The item's own unit: "bottle" or "can". */
  itemNoun: string;
  /** Cents per item, possibly fractional, or null when it cannot be worked out (a keg, or a case with no count). */
  perItemCents: number | null;
}

export function readVendorOfferCost(input: {
  orderUnit: string;
  costCents: number | undefined;
  bottlesPerCase: number | undefined;
  containerType: string;
}): VendorOfferCostReading | null {
  const orderUnit = input.orderUnit.trim().toLowerCase();
  if (orderUnit.length === 0) return null;
  const itemNoun = input.containerType === "can" ? "can" : "bottle";
  let perItemCents: number | null = null;
  if (input.costCents !== undefined) {
    if (orderUnit === "case") {
      perItemCents =
        input.bottlesPerCase !== undefined && input.bottlesPerCase > 0
          ? input.costCents / input.bottlesPerCase
          : null;
    } else if (orderUnit === "bottle" || orderUnit === "can" || orderUnit === "unit") {
      perItemCents = input.costCents;
    }
  }
  return { orderUnitNoun: orderUnit, itemNoun, perItemCents };
}

/** A bottle of liquor or wine under $1 or over $500 is almost always a case price or a bottle size typed in the
 * wrong box, so the form asks before saving. Cents per bottle, so the limits are 100 and 50,000. */
export const IMPLAUSIBLE_BOTTLE_COST_LOW_CENTS = 100;
export const IMPLAUSIBLE_BOTTLE_COST_HIGH_CENTS = 50_000;

export function isImplausibleBottleCost(perBottleCents: number): boolean {
  return (
    perBottleCents < IMPLAUSIBLE_BOTTLE_COST_LOW_CENTS ||
    perBottleCents > IMPLAUSIBLE_BOTTLE_COST_HIGH_CENTS
  );
}
